import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, MessageSquare, CreditCard, Smartphone, Wallet, DollarSign } from "lucide-react";
import MapMock from "@/components/customer/MapMock";
import AddressAutocomplete from "@/components/maps/AddressAutocomplete";

const METHODS = [
  { key: "card", label: "Credit Card", icon: CreditCard },
  { key: "apple_pay", label: "Apple Pay", icon: Smartphone },
  { key: "google_pay", label: "Google Pay", icon: Wallet },
  { key: "cash", label: "Cash", icon: DollarSign },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const { cart, clear } = useCart();
  const [address, setAddress] = useState(user?.address || "12 Grand Anse Rd, St. George's");
  const [dropoffCoords, setDropoffCoords] = useState(null); // { lat, lng } from Places pick
  const [phone, setPhone] = useState(user?.phone || "+1 473 555 0198");
  const [instructions, setInstructions] = useState("");
  const [method, setMethod] = useState("card");
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [fulfillment, setFulfillment] = useState("delivery"); // delivery | pickup
  // Guest checkout — collected only when the user isn't signed in.
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");

  const tip = state?.tip ?? 3;
  const promo = state?.promo || null;

  useEffect(() => {
    if (!cart.restaurant || !cart.items.length) { navigate("/cart"); return; }
    api.post("/orders/quote", {
      restaurant_id: cart.restaurant.restaurant_id,
      items: cart.items.map((i) => ({ menu_item_id: i.item_id, quantity: i.quantity })),
      tip, promo_code: promo, address: "", phone: "", payment_method: method,
      fulfillment_mode: fulfillment,
      dropoff_lat: dropoffCoords?.lat,
      dropoff_lng: dropoffCoords?.lng,
    }).then((r) => setQuote(r.data)).catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line
  }, [fulfillment, dropoffCoords?.lat, dropoffCoords?.lng]);

  const placeOrder = async () => {
    // For pickup orders the customer picks up at the restaurant — no delivery address needed.
    if (fulfillment === "delivery" && !address.trim()) { toast.error("Delivery address required"); return; }
    if (!phone.trim()) { toast.error("Phone number required"); return; }
    if (!user && (!guestEmail.trim() || !guestName.trim())) {
      toast.error("Please provide your email and name to check out as a guest");
      return;
    }
    setPlacing(true);
    try {
      const { data: order } = await api.post("/orders", {
        restaurant_id: cart.restaurant.restaurant_id,
        items: cart.items.map((i) => ({ menu_item_id: i.item_id, quantity: i.quantity })),
        address: fulfillment === "pickup" ? cart.restaurant.name : address,
        phone, instructions, tip, promo_code: promo, payment_method: method,
        dropoff_lat: dropoffCoords?.lat,
        dropoff_lng: dropoffCoords?.lng,
        fulfillment_mode: fulfillment,
        guest_email: !user ? guestEmail.trim() : undefined,
        guest_name: !user ? guestName.trim() : undefined,
      });
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", {
          order_id: order.order_id,
          origin_url: window.location.origin,
        });
        clear();
        window.location.href = data.checkout_url;
      } else {
        clear();
        toast.success("Order placed!");
        navigate(`/track/${order.order_id}`);
      }
    } catch (e) { toast.error(formatApiError(e)); setPlacing(false); }
  };

  if (!cart.restaurant) return null;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 sm:py-10 pb-32">
      <button onClick={() => navigate(-1)} data-testid="checkout-back-btn" className="flex items-center gap-2 text-neutral-600">
        <ArrowLeft className="w-4 h-4" /> Back to cart
      </button>
      <h1 className="font-display text-4xl font-bold mt-4">Checkout</h1>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Fulfillment toggle — Delivery vs Pickup */}
          <div className="bg-white rounded-3xl card-shadow p-2 flex" data-testid="fulfillment-toggle">
            {["delivery", "pickup"].map((m) => (
              <button
                key={m}
                onClick={() => setFulfillment(m)}
                data-testid={`fulfillment-${m}`}
                className={`flex-1 py-3 rounded-2xl text-sm font-black uppercase tracking-widest transition ${
                  fulfillment === m ? "bg-eatup-green text-white shadow-md" : "text-neutral-500 hover:text-eatup-charcoal"
                }`}
              >
                {m === "delivery" ? "Delivery" : "Pickup"}
              </button>
            ))}
          </div>

          {/* Guest checkout — only shown when the caller isn't signed in */}
          {!user && (
            <div className="bg-white rounded-3xl card-shadow p-6" data-testid="guest-checkout">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-eatup-green">Guest checkout</div>
                  <h3 className="font-display font-bold text-xl mt-1">Tell us where to send your receipt</h3>
                </div>
                <button
                  onClick={() => navigate("/login")}
                  data-testid="guest-signin-btn"
                  className="text-xs font-bold uppercase tracking-widest text-eatup-green hover:underline"
                >
                  Sign in
                </button>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="guest-name-input"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Full name"
                  className="w-full px-4 py-3 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white outline-none"
                />
                <input
                  data-testid="guest-email-input"
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="Email for receipt"
                  className="w-full px-4 py-3 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white outline-none"
                />
              </div>
            </div>
          )}

          {/* Delivery / Pickup address block */}
          <div className="bg-white rounded-3xl card-shadow p-6">
            <div className="text-xs uppercase tracking-widest font-bold text-eatup-green">
              {fulfillment === "pickup" ? "Pickup" : "Delivery"}
            </div>
            <h3 className="font-display font-bold text-xl mt-1">
              {fulfillment === "pickup"
                ? `Pick up at ${cart.restaurant.name}`
                : "Where should we bring it?"}
            </h3>

            {fulfillment === "delivery" ? (
              <>
                <div className="mt-4">
                  <MapMock height={180} />
                </div>
                <div className="mt-4 space-y-3">
                  <AddressAutocomplete
                    value={address}
                    onChange={(v) => { setAddress(v); setDropoffCoords(null); }}
                    onPlaceSelected={({ lat, lng }) => setDropoffCoords({ lat, lng })}
                    placeholder="Delivery address"
                    testId="checkout-address-input"
                  />
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      data-testid="checkout-phone-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Contact number"
                      className="w-full pl-11 pr-4 py-3 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none"
                    />
                  </div>
                  <div className="relative">
                    <MessageSquare className="w-4 h-4 absolute left-4 top-4 text-neutral-400" />
                    <textarea
                      data-testid="checkout-instructions-input"
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="Delivery instructions (gate code, apartment, etc.)"
                      rows={2}
                  className="w-full pl-11 pr-4 py-3 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none resize-none"
                />
              </div>
            </div>
              </>
            ) : (
              <div className="mt-4 space-y-3" data-testid="pickup-details">
                <div className="rounded-2xl bg-eatup-yellow/10 border border-eatup-yellow/30 p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-eatup-yellow text-eatup-charcoal flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div className="flex-1 text-sm text-neutral-700">
                    <div className="font-bold text-eatup-charcoal">{cart.restaurant.name}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      We'll text you at the number below the moment your order is ready to grab.
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    data-testid="checkout-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Contact number for pickup notification"
                    className="w-full pl-11 pr-4 py-3 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Payment */}
          <div className="bg-white rounded-3xl card-shadow p-6">
            <div className="text-xs uppercase tracking-widest font-bold text-eatup-green">Payment</div>
            <h3 className="font-display font-bold text-xl mt-1">How would you like to pay?</h3>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {METHODS.map((m) => {
                const Icon = m.icon;
                const active = method === m.key;
                return (
                  <button
                    key={m.key}
                    data-testid={`pay-${m.key}`}
                    onClick={() => setMethod(m.key)}
                    className={`p-4 rounded-2xl border transition ${
                      active
                        ? "border-eatup-green bg-eatup-green/5 ring-2 ring-eatup-green/20"
                        : "border-neutral-200 hover:border-neutral-300"
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-2 ${active ? "text-eatup-green" : "text-neutral-500"}`} />
                    <div className="text-sm font-semibold">{m.label}</div>
                  </button>
                );
              })}
            </div>
            {method === "card" && (
              <p className="mt-3 text-xs text-neutral-500">You'll be redirected to Stripe. Test card: <span className="font-mono">4242 4242 4242 4242</span></p>
            )}
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-fit bg-white rounded-3xl card-shadow p-6">
          <h3 className="font-display font-bold text-xl">Order summary</h3>
          <div className="mt-4 flex items-center gap-3 pb-3 border-b border-neutral-100">
            <img src={cart.restaurant.image_url} alt={cart.restaurant.name} className="w-14 h-14 rounded-xl object-cover" />
            <div>
              <div className="font-semibold">{cart.restaurant.name}</div>
              <div className="text-xs text-neutral-500">{cart.items.reduce((s, i) => s + i.quantity, 0)} items</div>
            </div>
          </div>
          {quote ? (
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Subtotal" value={`EC$${quote.subtotal.toFixed(2)}`} />
              <Row
                label={quote.distance_km ? `Delivery (${quote.distance_km.toFixed(1)} km)` : "Delivery"}
                value={`EC$${quote.delivery_fee.toFixed(2)}`}
              />
              <Row label="Platform fee" value={`EC$${(quote.platform_flat_fee ?? quote.service_charge).toFixed(2)}`} />
              {quote.small_order_fee > 0 && (
                <Row label="Small-order fee" value={`EC$${quote.small_order_fee.toFixed(2)}`} />
              )}
              <Row label="Tax" value={`EC$${quote.tax.toFixed(2)}`} />
              <Row label="Tip" value={`EC$${quote.tip.toFixed(2)}`} />
              {quote.discount > 0 && <Row label="Promo" value={`- EC$${quote.discount.toFixed(2)}`} highlight />}
              <div className="h-px bg-neutral-100 my-2" />
              <div className="flex items-center justify-between font-display font-bold text-lg">
                <span>Total</span>
                <span data-testid="checkout-total">EC${quote.total.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="mt-4 skeleton h-24 rounded-xl" />
          )}
          <button
            data-testid="place-order-btn"
            onClick={placeOrder}
            disabled={placing || !quote}
            className="w-full btn-primary mt-6 disabled:opacity-60"
          >
            {placing ? "Placing…" : method === "card" ? `Pay & Place order` : `Place order`}
          </button>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-neutral-500">{label}</span>
      <span className={`font-semibold tabular ${highlight ? "text-eatup-green" : ""}`}>{value}</span>
    </div>
  );
}
